/**
 * ForecastSectionCard — THE revenue section (ARR) in the Deal-Detail layout.
 *
 * CARD SET (quick-260917-nfy, mirroring the CEO artifact v25–v28 per D-13a):
 *
 *   1. Product ARR Forecast — the headline card, eyebrow INSIDE the card, four
 *      PRODUCT-ONLY figures Won + Commit + Upside = Total in whole dollars, and
 *      a raw signed over/under against the ARR budget under the Total alone
 *      (D-06). No percent-of-budget captions, no budget line, no verdict pill,
 *      no sub-group line. Product = the New + Expansion streams; Managed
 *      Services is deliberately excluded because the artifact's Total
 *      over/under is product total less the ARR budget.
 *   2. Deal Waterfall.
 *   3. Deal Detail (collapsible).
 *   4. Managed Services ARR Forecast — its own eyebrowed card with one figure
 *      captioned 'Total'. No budget of its own (D-05).
 *
 * There is NO section heading outside the cards: the former 'PRODUCT (ARR)' h2
 * and its gradient rule are gone, and the `<section>`'s accessible name is the
 * Product card's eyebrow. The sub-group line went with the budget line —
 * planner discretion under D-13a/D-13d: the artifact's card has no such row,
 * the equation strip above already shows the three streams, and a line naming
 * Managed under a product-only card would contradict the figures above it.
 *
 * PLAN 08 TASK 2 — the one-line weight legend is gone along with the prop that
 * fed it. It stated a fixed Best Case percentage, which contradicts every figure
 * on the page now that the percentage is a per-viewer toggle, and the equation
 * strip above already shows how each stream is composed (D-13d).
 *
 * PHASE 05.3 D-10 — this card renders ONE shape. The Implementation Services
 * (T&M) variant is gone: the awaiting-upload empty state, the services flag and
 * every branch it selected, and the single-tier / single-weight props it
 * forwarded to the waterfall and the table. The shared contract no longer
 * carries a second section, so the branches had nothing left to select on.
 *
 * D-13d — the two-line tier-definitions explainer that sat under the headline is
 * also gone. It narrated the figure rather than labelling it.
 *
 * `upsidePct` is RECEIVED, never sourced — see the prop's own note below.
 */
import { useState } from 'react';

import { streams } from '../../lib/forecast-weighting.js';
import { formatAmount, signed } from '../../lib/format.js';
import { Card } from '../ui/Card.js';

import { ForecastDealDetailTable } from './ForecastDealDetailTable.js';
import { ForecastUpsideToggle } from './ForecastUpsideToggle.js';
import { ForecastWaterfall } from './ForecastWaterfall.js';

import type { ForecastPeriod, ForecastSection } from '@saledash/shared';
import type { UpsidePct } from '@saledash/shared/forecast/weighting';

const knob = (u: UpsidePct, stream: 'product' | 'managed'): number => (typeof u === 'number' ? u : u[stream]);

/* v39 (John, 2026-10-01): the Product and Managed Services headline cards read two
   sizes smaller than before (text-lg figures) with tighter padding (`!p-3`) so the
   box carries less white space but keeps a margin. Mirrors the artifact v39. */
/** The in-card eyebrow the artifact puts on every headline card (quick-260917-nfy). */
const EYEBROW_CLASS = 'text-[0.66rem] font-semibold uppercase tracking-wider text-brand-700';
/** Caption over each headline figure. */
const CAPTION_CLASS = 'text-[0.64rem] font-medium uppercase tracking-wider text-ink-400';
/** The whole-dollar headline figure. */
const FIGURE_CLASS = 'text-lg font-bold tabular-nums text-ink-900';
/** The aria-hidden + / = between figures. */
const OPERATOR_CLASS = 'pt-5 text-lg font-light text-ink-300';

interface ForecastSectionCardProps {
  sectionKey: string;
  section: ForecastSection;
  period?: ForecastPeriod;
  /**
   * The viewer's Upside % (05.3 D-03), RECEIVED as a prop and forwarded to the
   * deal table. This card must never source the stored default itself: the PAGE
   * is the sole supplier of the live value, so when the page swaps its
   * expression for toggle state the deal-table footer moves with it. A card that
   * sourced its own default would leave the footer frozen while the equation
   * strip above it moved.
   */
  upsidePct: UpsidePct;
  /** quick-261001-nym — the two knobs live on this card's Product and Managed boxes. */
  onUpsideChange?: ((stream: 'product' | 'managed', next: number) => void) | undefined;
  /** Closed quarter: the knobs are grayed and disabled. */
  upsideDisabled?: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function ForecastSectionCard({
  sectionKey,
  section,
  upsidePct,
  onUpsideChange,
  upsideDisabled,
  isLoading,
  isError,
}: ForecastSectionCardProps): React.JSX.Element {
  // quick-260917-nfy — no heading outside the cards. The id this section is
  // labelled by sits on the Product card's eyebrow, so the section's accessible
  // name is 'Product ARR Forecast'.
  return (
    <section aria-labelledby={`forecast-section-${sectionKey}`} className="space-y-3">
      <ForecastSectionBody
        sectionKey={sectionKey}
        section={section}
        upsidePct={upsidePct}
        onUpsideChange={onUpsideChange}
        upsideDisabled={upsideDisabled}
        isLoading={isLoading}
        isError={isError}
      />
    </section>
  );
}

function ForecastSectionBody({
  sectionKey,
  section,
  upsidePct,
  onUpsideChange,
  upsideDisabled,
  isLoading,
  isError,
}: {
  sectionKey: string;
  section: ForecastSection;
  upsidePct: UpsidePct;
  /** quick-261001-nym — the two knobs live on this card's Product and Managed boxes. */
  onUpsideChange?: ((stream: 'product' | 'managed', next: number) => void) | undefined;
  /** Closed quarter: the knobs are grayed and disabled. */
  upsideDisabled?: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
}): React.JSX.Element {
  const targetValue = section.target?.value ?? null;
  const deals = section.deals ?? [];
  const productDeals = deals.filter((d) => d.sub_group !== 'Managed Services');
  const managedDeals = deals.filter((d) => d.sub_group === 'Managed Services');
  const [showDeals, setShowDeals] = useState(true);

  // ONE BASIS FOR EVERY FIGURE ON THIS CARD (05.3 plan 08 Task 1).
  //
  // Every headline figure derives from `deals[]` through the shared weighting
  // module at the LIVE Upside %. There is deliberately NO fallback to the
  // section's server-weighted aggregates: those are baked at recompute time
  // against the STORED grid, so they cannot move with the toggle, and a fallback
  // path would silently show a frozen figure the moment `deals` were absent — a
  // stale number sitting where a live one belongs, with nothing on screen to
  // distinguish them (RESEARCH Pitfall 2: the two are built from different
  // queries and can differ slightly even at rest).
  //
  // quick-260917-nfy — D-13c: the SAME `streams()` partition the equation strip
  // reads. Product = New + Expansion; Managed Services is its own card below.
  // The artifact's Product Total over/under is product total less the ARR
  // budget (+$233,500 at 50% while the strip's combined figure reads +$632,500),
  // so Managed is EXCLUDED from these four figures on purpose.
  const st = streams(deals, upsidePct);
  const productWon = st.newBiz.won + st.expansion.won;
  const productCommit = st.newBiz.commit + st.expansion.commit;
  const productUpside = st.newBiz.upside + st.expansion.upside;
  // Every product deal's contribution, zero-weighted ones included at zero
  // rather than dropped (05.1 D-22).
  const productTotal = st.newBiz.total + st.expansion.total;
  const productDelta = targetValue === null ? null : productTotal - targetValue;

  if (isLoading || isError) {
    // Minimal placeholder; the cache-warming / error banners are handled upstream
    // by the page (503 → amber banner). Keep the headline shell stable.
    return (
      <Card data-testid={`forecast-headline-${sectionKey}`} className="!border-2 !border-brand-500 !p-3">
        <p className="py-6 text-center text-sm text-ink-500">Loading forecast…</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Product ARR Forecast headline — the artifact's `.card.headline`: a 2px
          brand-500 outline, eyebrow inside, Won + Commit + Upside = Total in
          whole dollars, no pill (quick-260917-nfy). */}
      <Card data-testid={`forecast-headline-${sectionKey}`} className="!border-2 !border-brand-500 !p-3">
        <p id={`forecast-section-${sectionKey}`} className={EYEBROW_CLASS}>
          Product ARR Forecast
        </p>

        <div className="mt-0.5 flex flex-wrap items-start gap-3">
          <div data-testid={`forecast-won-${sectionKey}`}>
            <p className={CAPTION_CLASS}>Won</p>
            <p className={FIGURE_CLASS}>{formatAmount(productWon)}</p>
          </div>
          <span aria-hidden className={OPERATOR_CLASS}>
            +
          </span>
          <div data-testid={`forecast-commit-${sectionKey}`}>
            <p className={CAPTION_CLASS}>Commit</p>
            <p className={FIGURE_CLASS}>{formatAmount(productCommit)}</p>
          </div>
          <span aria-hidden className={OPERATOR_CLASS}>
            +
          </span>
          <div data-testid={`forecast-upside-${sectionKey}`}>
            <p className={CAPTION_CLASS}>Upside</p>
            <p className={FIGURE_CLASS}>{formatAmount(productUpside)}</p>
          </div>
          <span aria-hidden className={OPERATOR_CLASS}>
            =
          </span>
          <div data-testid={`forecast-weighted-total-${sectionKey}`}>
            <p className={CAPTION_CLASS}>Total</p>
            <p className={FIGURE_CLASS}>{formatAmount(productTotal)}</p>
            {/* D-06 — the raw signed over/under against the ARR budget, under the
                Total ONLY (the artifact's `.delta.pos/.neg`). No percent caption
                here or under any other figure. */}
            {productDelta !== null ? (
              <p
                className={`text-[0.7rem] font-semibold tabular-nums ${
                  productDelta < 0 ? 'text-bad-text' : 'text-good-text'
                }`}
                data-testid={`forecast-budget-delta-${sectionKey}`}
              >
                {signed(productDelta)} vs budget
              </p>
            ) : (
              /* quick-261001-ibu — no seeded budget for this quarter (FY2027-Q1
                 while the FY2027 targets are TBD): say so, as the strip does. */
              <p className="text-[0.7rem] text-ink-500" data-testid={`forecast-budget-tbd-${sectionKey}`}>
                Budget TBD
              </p>
            )}
          </div>
          {/* quick-261001-nym — the Product knob: to the right of the figures, one
              size smaller than the dollar amounts, scaling Product Best Case only. */}
          {onUpsideChange && (
            <div className="ml-auto self-start">
              <ForecastUpsideToggle
                value={knob(upsidePct, 'product')}
                onChange={(v) => { onUpsideChange('product', v); }}
                appliesTo="Product Best Case deals"
                disabled={upsideDisabled}
                testId="forecast-upside-toggle"
              />
            </div>
          )}
        </div>
      </Card>

      {/* quick-260921-f73 — the waterfall is now ONE filling bar: Committed then
          Upside sums against a dotted budget line, any gap highlighted. */}
      {deals.length > 0 && (
        <Card>
          <p className="mb-3 text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
            Commit + Upside vs Budget
          </p>
          <ForecastWaterfall
            sectionKey={sectionKey}
            deals={deals}
            target={targetValue}
            upsidePct={upsidePct}
          />
        </Card>
      )}

      {/* The workbook Deal Detail table — live individual deals, collapsible. */}
      <Card>
        <button
          type="button"
          onClick={() => {
            setShowDeals((v) => !v);
          }}
          aria-expanded={showDeals}
          className="flex w-full items-center justify-between text-left"
          data-testid={`forecast-dealdetail-toggle-${sectionKey}`}
        >
          <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
            Deal Detail
          </span>
          <svg
            className={`h-4 w-4 text-ink-400 transition-transform ${showDeals ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {showDeals && (
          <div className="mt-3">
            {/* quick-261001-nym — Product deals only; Managed Services has its own
                table under its own card, exactly as the CEO artifact lays it out. */}
            <ForecastDealDetailTable
              sectionKey={sectionKey}
              deals={productDeals}
              target={targetValue}
              upsidePct={upsidePct}
            />
          </div>
        )}
      </Card>

      {/* Managed Services ARR Forecast — its own eyebrowed card, one 'Total'
          figure from the SAME streams() partition, no budget of its own (D-05,
          D-12). Last in the column so the YTD box still sits below Managed
          Services (D-19). quick-260917-nfy. */}
      <Card data-testid="forecast-headline-managed" className="!border-2 !border-brand-500 !p-3">
        <p className={EYEBROW_CLASS}>Managed Services ARR Forecast</p>
        <div className="mt-0.5 flex flex-wrap items-start gap-3">
          <div>
            <p className={CAPTION_CLASS}>Total</p>
            <p className={FIGURE_CLASS} data-testid={`forecast-managed-total-${sectionKey}`}>
              {formatAmount(st.managed.total)}
            </p>
          </div>
          {/* quick-261001-nym — the Managed knob, same rules as the Product one. */}
          {onUpsideChange && (
            <div className="ml-auto self-start">
              <ForecastUpsideToggle
                value={knob(upsidePct, 'managed')}
                onChange={(v) => { onUpsideChange('managed', v); }}
                appliesTo="Managed Services Best Case deals"
                disabled={upsideDisabled}
                testId="forecast-upside-toggle-managed"
              />
            </div>
          )}
        </div>
      </Card>

      {/* quick-261001-nym — the Managed Services deal table, under its own card. */}
      <Card>
        <p className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">Managed Services Deal Detail</p>
        <div className="mt-3">
          <ForecastDealDetailTable
            sectionKey="managed"
            deals={managedDeals}
            target={null}
            upsidePct={upsidePct}
          />
        </div>
      </Card>
    </div>
  );
}
